import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Address, B2BUser, PaymentDetails, UserPaymentService} from '@spartacus/core';
import {combineLatest} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import {PaymentTypeAction} from '@app/model/order/dcp-order.model';
import {DcpCcPspService, PaymentProvider} from '@app/payment/services/dcp-cc-psp.service';
import {DcpCheckoutPaymentService} from '@app/payment/services/dcp-checkout-payment.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {Cart} from '@spartacus/cart/base/root';
import {CyberSourceService} from '@app/payment/services/cybersource/cyber-source.service';

@UntilDestroy()
@Component({
  selector: 'dcp-payment-method-creditcard',
  templateUrl: './payment-method-creditcard.component.html',
  styleUrls: ['./payment-method-creditcard.component.scss'],
})
export class PaymentMethodCreditcardComponent implements OnInit {
  @Input() invoiceContacts: B2BUser[];
  @Input() invoiceAddresses: Address[];
  @Input() hidden: Boolean;
  showCCError: boolean;
  userPaymentMethods: PaymentDetails[];
  showCreditCardDropdown: boolean = true;
  currencyIso: string = '';
  currencyAmount: number = 1;
  selectedPaymentDetailsId: string;
  addNewCardAction: PaymentTypeAction = PaymentTypeAction.ADD_CARD;
  paymentProviderId: string = '';
  paymentProvider = PaymentProvider;
  paymentProviderFormRefreshMsg: string;
  private currentCart: Cart;
  private minimumChargeMap: Map<string, number> = new Map([
    ['USD', 1],
    ['AED', 2],
    ['AUD', 1],
    ['BGN', 1],
    ['BRL', 1],
    ['CAD', 1],
    ['CHF', 1],
    ['CZK', 15],
    ['DKK', 2.50],
    ['EUR', 1],
    ['GBP', 1],
    ['HKD', 4],
    ['HUF', 175],
    ['INR', 1],
    ['JPY', 50],
    ['MXN', 10],
    ['MYR', 2],
    ['NOK', 3],
    ['NZD', 1],
    ['PLN', 2],
    ['RON', 2],
    ['SEK', 3],
    ['SGD', 1],
    ['THB', 10]
  ]);

  constructor(
    private dcpCcPspService: DcpCcPspService,
    private cdRef: ChangeDetectorRef,
    private dcpActiveCartService: DcpActiveCartService,
    private dcpCheckoutPaymentService: DcpCheckoutPaymentService,
    private userPaymentService: UserPaymentService,
    private cyberSourceService: CyberSourceService,
  ) {
  }

  ngOnInit() {
    this.subscribeToAddNewCardComponentStatus();
    this.subscribeToCurrentCart();
    this.subscribeToPaymentMethods();
    this.subscribeToCcErrorMessageStatus();
    this.subscribeToRefreshTimers();
  }

  subscribeToCurrentCart(): void {
    this.dcpActiveCartService.getActiveCart().pipe(untilDestroyed(this)).subscribe((cart: Cart) => {
      this.currencyIso = cart?.totalPriceWithTax?.currencyIso;
      this.currencyAmount = this.minimumChargeMap.get((this.currencyIso || 'USD').toUpperCase());
      this.currentCart = cart;
      this.dcpCcPspService.setPspMerchantId(this.currentCart);
    });
  }

  public fetchLastCreditCardDigits(fullCreditCardDigits: string): string {
    return fullCreditCardDigits.slice(-4);
  }

  public selectedCreditCardValueChanged(event): void {
    if (event.detail.item.value === PaymentTypeAction.ADD_CARD) {
      this.showCreditCardDropdown = false;
      this.dcpCcPspService.openAddNewCreditCardComponent(this.currencyIso);
    } else {
      this.dcpCheckoutPaymentService.setCurrentPaymentDetails(this.userPaymentMethods.find(paymentDetails => paymentDetails.id === event.detail.item.value));
    }
    this.cdRef.detectChanges();
  }

  private subscribeToAddNewCardComponentStatus(): void {
    this.dcpCcPspService.isAddNewCreditCardComponentClosed().pipe(
      untilDestroyed(this)
    ).subscribe(isClosed => {
      this.showCreditCardDropdown = isClosed;
      this.cdRef.detectChanges();
    });
  }

  private subscribeToPaymentMethods() {
    this.userPaymentService.loadPaymentMethods();
    combineLatest([
      this.dcpCcPspService.getCurrentCartPaymentProviderId(),
      this.userPaymentService.getPaymentMethodsLoadedSuccess().pipe(
        filter(Boolean),
        untilDestroyed(this)
      )
    ]).pipe(untilDestroyed(this)).subscribe(([currentPaymentProvider, paymentMethodsReady]) => {
      this.paymentProviderId = currentPaymentProvider;
      this.userPaymentService.getPaymentMethods().pipe(take(1), untilDestroyed(this)).subscribe((paymentDetailsArray) => {
        this.userPaymentMethods = paymentDetailsArray.filter(payment => this.isNeedToShowCcInDropdown(payment, currentPaymentProvider));
        if (this.userPaymentMethods?.length) {
          const previouslySelected = this.userPaymentMethods.find((card) => card.id === this.currentCart?.paymentInfo?.id);
          this.selectedPaymentDetailsId = previouslySelected ? previouslySelected.id : this.userPaymentMethods[0]?.id;

          if (!previouslySelected && this.selectedPaymentDetailsId) {
            // if paymentInfo is not set and customer have any payment method persisted already then set it as current payment method
            this.dcpCheckoutPaymentService.setCurrentPaymentDetails(this.userPaymentMethods.find(paymentDetails => paymentDetails.id === this.selectedPaymentDetailsId));
          }

          this.dcpCheckoutPaymentService.getCurrentPaymentDetails().pipe(
            filter(createdPaymentDetails => !!createdPaymentDetails?.id),
            take(1),
            untilDestroyed(this)).subscribe((createdPaymentDetails) => {
            const newAddedCard = this.userPaymentMethods.find((card) => card.id === createdPaymentDetails.id);
            if (newAddedCard) {
              this.selectedPaymentDetailsId = newAddedCard.id;
              this.cdRef.detectChanges();
            }
          });
        } else {
          this.dcpCcPspService.openAddNewCreditCardComponent(this.currencyIso);
        }
        this.cdRef.detectChanges();
      });
    });
  }

  private subscribeToCcErrorMessageStatus() {
    this.dcpCcPspService.getErrorMessageStatus().pipe(untilDestroyed(this)).subscribe(status => {
      this.showCCError = status;
      this.cdRef.detectChanges();
    });
  }

  private subscribeToRefreshTimers() {
    this.cyberSourceService.shouldRefreshForm$().pipe(untilDestroyed(this)).subscribe(refresh => {
      if (this.paymentProviderId === this.paymentProvider.CYBERSOURCE && refresh) {
        this.paymentProviderId = undefined;
        this.cdRef.detectChanges();
        this.paymentProviderFormRefreshMsg = 'checkout.digitalNativeCheckout.paymentType.cc.errors.sessionExpired';
        this.paymentProviderId = PaymentProvider.CYBERSOURCE;
        this.cdRef.detectChanges();
      }
    });
  }

  private isNeedToShowCcInDropdown(payment: PaymentDetails, currentPaymentProvider: string): boolean {
    return currentPaymentProvider === PaymentProvider.MOCK_STRIPE || payment.paymentProvider === currentPaymentProvider;
  }

}
